import React, { useEffect, useState } from "react";
import { toast } from "sonner";
import { Ticket, Pencil, Trash2, Copy, BarChart3 } from "lucide-react";
import api, { formatApiError } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Switch } from "@/components/ui/switch";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { PreviewButton } from "@/components/AnnouncementPreview";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";

const ROLE = { broker: "Mediator", owner: "Owner" };

function PromoReport({ promo, onClose }) {
  const [uses, setUses] = useState(null);
  useEffect(() => { api.get(`/admin/promo-codes/${promo.id}/uses`).then((r) => setUses(r.data.uses)); }, [promo.id]);
  return (
    <Dialog open onOpenChange={(o) => { if (!o) { onClose(); } }}>
      <DialogContent className="max-w-2xl max-h-[85vh] overflow-y-auto" data-testid="promo-report-dialog">
        <DialogHeader><DialogTitle>{promo.code} · Usage report</DialogTitle></DialogHeader>
        <div className="grid grid-cols-3 gap-3">
          <div className="rounded-xl bg-secondary p-3"><p className="text-xs text-brand-muted">Times used</p><p className="font-heading font-bold text-xl" data-testid="report-uses">{promo.report.uses}</p></div>
          <div className="rounded-xl bg-secondary p-3"><p className="text-xs text-brand-muted">Total discount</p><p className="font-heading font-bold text-xl" data-testid="report-discount">₹{promo.report.discount}</p></div>
          <div className="rounded-xl bg-secondary p-3"><p className="text-xs text-brand-muted">On confirmed payments</p><p className="font-heading font-bold text-xl" data-testid="report-confirmed">₹{promo.report.confirmed_discount}</p></div>
        </div>
        {!uses ? <p className="text-sm text-brand-muted">Loading...</p> : uses.length === 0 ? <p className="text-sm text-brand-muted py-6 text-center">Nobody has used this code yet.</p> : (
          <div className="space-y-2">
            {uses.map((u) => (
              <div key={u.id} className="border border-brand-line rounded-xl px-3 py-2.5 flex flex-wrap justify-between gap-2" data-testid={`report-use-${u.id}`}>
                <div className="min-w-0">
                  <p className="text-sm font-medium text-brand-ink">{u.user_name} <span className="text-xs text-brand-muted">· {ROLE[u.role] || u.role}</span></p>
                  <p className="text-xs text-brand-muted">{[u.email, u.phone].filter(Boolean).join(" · ")}</p>
                  <p className="text-xs text-brand-muted">{u.kind === "listing" ? `Listing fee${u.property_title ? `: ${u.property_title}` : ""}` : "Subscription"} · {u.used_at ? new Date(u.used_at).toLocaleString("en-IN", { timeZone: "Asia/Kolkata" }) : ""}</p>
                </div>
                <div className="text-right">
                  <p className="text-sm"><span className="line-through text-brand-muted mr-1">₹{u.original}</span><span className="font-semibold">₹{u.paid}</span></p>
                  <p className="text-xs text-brand-sale font-semibold">−₹{u.discount} · <span className="capitalize">{u.status}</span></p>
                </div>
              </div>
            ))}
          </div>
        )}
      </DialogContent>
    </Dialog>
  );
}

const EMPTY = { code: "", description: "", discount_type: "percent", discount_value: 10, applies_to: "all", max_uses: 0, valid_from: "", valid_until: "", active: true };
const APPLIES = { all: "All payments", subscription: "Mediator subscription", listing: "Owner listing fee" };

function PromoForm({ form, setForm, editing, onSave, onCancel }) {
  const set = (k) => (e) => setForm((f) => ({ ...f, [k]: e.target.value }));
  return (
    <div className="bg-card border border-brand-line rounded-2xl p-6 h-fit space-y-3">
      <h3 className="font-heading font-semibold text-lg flex items-center gap-2"><Ticket className="w-5 h-5 text-brand-terracotta" /> {editing ? "Edit Promo Code" : "Create Promo Code"}</h3>
      <div><Label>Code</Label><Input value={form.code} onChange={(e) => setForm((f) => ({ ...f, code: e.target.value.toUpperCase() }))} placeholder="e.g. DIWALI50" className="mt-1.5" data-testid="promo-code" /></div>
      <div><Label>Description <span className="text-brand-muted text-xs">(optional)</span></Label><Input value={form.description} onChange={set("description")} placeholder="e.g. Festive offer for new mediators" className="mt-1.5" data-testid="promo-description" /></div>
      <div className="grid grid-cols-2 gap-3">
        <div><Label>Discount type</Label>
          <Select value={form.discount_type} onValueChange={(v) => setForm((f) => ({ ...f, discount_type: v }))}><SelectTrigger className="mt-1.5" data-testid="promo-type"><SelectValue /></SelectTrigger>
            <SelectContent><SelectItem value="percent">Percent (%)</SelectItem><SelectItem value="flat">Flat amount (₹)</SelectItem></SelectContent></Select></div>
        <div><Label>Discount</Label><Input type="number" min="1" value={form.discount_value} onChange={set("discount_value")} className="mt-1.5" data-testid="promo-value" /></div>
      </div>
      <div className="grid grid-cols-2 gap-3">
        <div><Label>Applies to</Label>
          <Select value={form.applies_to} onValueChange={(v) => setForm((f) => ({ ...f, applies_to: v }))}><SelectTrigger className="mt-1.5" data-testid="promo-applies"><SelectValue /></SelectTrigger>
            <SelectContent>{Object.keys(APPLIES).map((k) => <SelectItem key={k} value={k}>{APPLIES[k]}</SelectItem>)}</SelectContent></Select></div>
        <div><Label>Max uses <span className="text-brand-muted text-xs">(0 = unlimited)</span></Label><Input type="number" min="0" value={form.max_uses} onChange={set("max_uses")} className="mt-1.5" data-testid="promo-max-uses" /></div>
      </div>
      <div className="grid grid-cols-2 gap-3">
        <div><Label>Valid from</Label><Input type="date" value={form.valid_from} onChange={set("valid_from")} className="mt-1.5" data-testid="promo-from" /></div>
        <div><Label>Valid until</Label><Input type="date" value={form.valid_until} onChange={set("valid_until")} className="mt-1.5" data-testid="promo-until" /></div>
      </div>
      <div className="flex items-center gap-2"><Switch checked={form.active} onCheckedChange={(v) => setForm((f) => ({ ...f, active: v }))} data-testid="promo-active" /><span className="text-sm text-brand-ink">{form.active ? "Active" : "Inactive"}</span></div>
      <div className="flex flex-wrap gap-2 pt-1">
        <Button onClick={onSave} className="rounded-full bg-brand-terracotta flex-1" data-testid="promo-save"><Ticket className="w-4 h-4 mr-1.5" />{editing ? "Update Code" : "Create Code"}</Button>
        <PreviewButton kind="promo" data={form} testId="promo-preview" />
        {editing && <Button variant="outline" className="rounded-full" onClick={onCancel}>Cancel</Button>}
      </div>
    </div>
  );
}

export const PromoCodes = () => {
  const [promos, setPromos] = useState([]);
  const [form, setForm] = useState(EMPTY);
  const [editing, setEditing] = useState(null);
  const [report, setReport] = useState(null);
  const load = () => api.get("/admin/promo-codes").then((r) => setPromos(r.data.promos));
  useEffect(() => { load(); }, []);
  const reset = () => { setForm(EMPTY); setEditing(null); };
  const save = async () => {
    const body = { ...form, discount_value: Number(form.discount_value), max_uses: Number(form.max_uses) || 0 };
    try {
      if (editing) { await api.put(`/admin/promo-codes/${editing}`, body); } else { await api.post("/admin/promo-codes", body); }
      toast.success("Promo code saved"); reset(); load();
    } catch (e) { toast.error(formatApiError(e?.response?.data?.detail)); }
  };
  const remove = async (id) => {
    if (!window.confirm("Delete this promo code?")) { return; }
    try { await api.delete(`/admin/promo-codes/${id}`); toast.success("Promo code deleted"); load(); }
    catch (e) { toast.error(formatApiError(e?.response?.data?.detail)); }
  };
  const copy = async (c) => { try { await navigator.clipboard.writeText(c); toast.success("Code copied"); } catch (e) { toast.error("Could not copy"); } };
  return (
    <div className="grid lg:grid-cols-[420px_1fr] gap-6" data-testid="promo-codes">
      <PromoForm form={form} setForm={setForm} editing={editing} onSave={save} onCancel={reset} />
      <div>
        <h3 className="font-heading font-semibold text-lg mb-1">Promo Codes ({promos.length})</h3>
        <p className="text-sm text-brand-muted mb-4">Mediators and owners enter these on their Payments page to get a discount.</p>
        {promos.length === 0 ? <p className="text-brand-muted py-8 text-center">No promo codes yet.</p> : (
          <div className="space-y-3">
            {promos.map((p) => (
              <div key={p.id} className="bg-card border border-brand-line rounded-xl p-4 flex flex-wrap justify-between gap-3" data-testid={`promo-row-${p.id}`}>
                <div className="min-w-0">
                  <div className="flex items-center gap-2 flex-wrap">
                    <span className="font-heading font-bold tracking-wider text-brand-ink">{p.code}</span>
                    <span className="text-xs bg-brand-sale/10 text-brand-sale px-2 py-0.5 rounded-full">{p.discount_type === "flat" ? `₹${p.discount_value} off` : `${p.discount_value}% off`}</span>
                    <span className={`text-[11px] font-semibold px-2 py-0.5 rounded ${p.active ? "bg-brand-sale/10 text-brand-sale" : "bg-brand-muted/10 text-brand-muted"}`}>{p.active ? "Active" : "Inactive"}</span>
                  </div>
                  <p className="text-xs text-brand-muted mt-1">{APPLIES[p.applies_to]} · Used {p.used_count || 0}{p.max_uses ? ` / ${p.max_uses}` : ""} · Discount given ₹{p.report?.discount || 0}{(p.valid_from || p.valid_until) ? ` · ${p.valid_from || "…"} → ${p.valid_until || "…"}` : ""}</p>
                  {p.description && <p className="text-sm text-brand-ink/70 mt-1">{p.description}</p>}
                </div>
                <div className="flex items-start gap-2">
                  <Button size="sm" variant="outline" className="rounded-full" onClick={() => setReport(p)} data-testid={`promo-report-${p.id}`}><BarChart3 className="w-4 h-4 mr-1" />Report</Button>
                  <Button size="sm" variant="outline" className="rounded-full" onClick={() => copy(p.code)} data-testid={`promo-copy-${p.id}`}><Copy className="w-4 h-4" /></Button>
                  <Button size="sm" variant="outline" className="rounded-full" onClick={() => { setEditing(p.id); setForm({ ...EMPTY, ...p }); }} data-testid={`promo-edit-${p.id}`}><Pencil className="w-4 h-4" /></Button>
                  <Button size="sm" variant="outline" className="rounded-full text-brand-terracotta" onClick={() => remove(p.id)} data-testid={`promo-delete-${p.id}`}><Trash2 className="w-4 h-4" /></Button>
                </div>
              </div>
            ))}
          </div>
        )}
      </div>
      {report && <PromoReport promo={report} onClose={() => setReport(null)} />}
    </div>
  );
};
